import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFormValidationToast } from '../../../services/actionToast';
import { getFetchData, isRequestFailure, useFetch } from '../../../hooks/useFetch';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';
import { FormDialog } from '../../ui';
import { getFormattedLabel } from '../../../utils/FormatFunctions';

interface AssessmentFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  defaultValues?: any;
}

const schema = Yup.object().shape({
  rating_topics: Yup.array().min(1, 'Selecione ao menos um tópico.'),
});

const toDefaults = (values: any = {}) => ({
  rating_topics: [...(values?.rating_topics?.map((item) => getFormattedLabel(item)) ?? [])],
});

export const AssessmentFormDialog = ({
  isOpen,
  onClose,
  onSaved,
  defaultValues,
}: AssessmentFormDialogProps) => {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/ratings';
  const { requestApi } = useFetch(baseUrl);
  const {
    requestApi: reqApiTopics,
    data: dataTopics,
    loading: loadingTopics,
  } = useFetch('/v1/admin/rating/topics');
  const { data: optionsTopics, pagination: paginationTopics } = getFetchData(dataTopics);
  const isEdit = Boolean(defaultValues?.id);

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: toDefaults(defaultValues),
  });

  useEffect(() => {
    if (isOpen) reset(toDefaults(defaultValues));
  }, [isOpen, defaultValues, reset]);

  const onSubmit = async (values: any) => {
    const params = {
      ...values,
      rating_topics: values.rating_topics.map((item) => ({ id: item.value })),
    };
    const result = await requestApi(
      isEdit ? 'put' : 'post',
      params,
      isEdit ? `${baseUrl}/${defaultValues.id}` : null
    );
    if (!isRequestFailure(result)) {
      onSaved();
      onClose();
    }
  };

  return (
    <FormDialog
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Editar avaliação' : 'Nova avaliação'}
      description="Escolha os tópicos que compõem esta avaliação."
      onSubmit={handleSubmit(onSubmit, validationToast)}
      isLoading={isSubmitting}
      submitLabel={isEdit ? 'Salvar alterações' : 'Criar avaliação'}
    >
      <SelectInputForm
        name="rating_topics"
        label="Tópicos"
        options={[...(optionsTopics?.map((item) => getFormattedLabel(item)) ?? [])]}
        isLoading={loadingTopics}
        pagination={paginationTopics}
        getOptions={(filters) => reqApiTopics('get', { params: { ...filters } })}
        isMulti
        isRequired
        placeholder="Selecione os tópicos"
        control={control}
        errors={errors}
      />
    </FormDialog>
  );
};
